import * as TableauUI from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { makeTestId } from '../../utils/Declarative';
import { EventUtilsOptOut, PropsAndElementAttrs } from '../../utils/ReactHelpers';

export type StickerType = 'neutral' | 'red' | 'yellow' | 'green' | 'teal' | 'blue' | 'violet';

export type StickerProps = {
  /** The content to be displayed inside the Sticker */
  children: React.ReactChild;
  /** Set to true when rendering on a dark background. This affects border color of the Sticker when it is interactive (i.e. has a handler for `onClick`). */
  darkBackground?: boolean;
  /** Set to true to disable the Sticker when it is interactive (i.e. has a handler for `onClick`) */
  disabled?: boolean;
  /** The icon to be displayed to left of `children` when rendered. */
  icon?: React.ReactElement | null;
  /** The type of Sticker to display. */
  stickerType: StickerType;
  /** Test id to be attached to the root element with suffix `-StickerWidget` */
  testId?: string;
};

const iconStyleName = TypeStyle.style({
  $nest: {
    '& > :first-child': {
      ...PreventUserSelect,
      color: 'inherit',
      marginRight: 4,
    },
  }
});

/**
 * Sticker is a visual marker that represents a status or classification that users add to their content.
 * May be passed as the `triggerElement` of a `PopoverWidget` to render an interactive sticker.
 * */
export const StickerWidget: React.FC<PropsAndElementAttrs<StickerProps>> = props => {
  const { children, className, icon, testId, ...stickerProps } = props;

  return (
    <TableauUI.Sticker
      {...(stickerProps.onClick && EventUtilsOptOut)}
      data-tb-test-id={makeTestId(testId, 'StickerWidget')}
      {...stickerProps}
      children={
        <>
          {icon}
          {children}
        </>
      }
      className={TypeStyle.classes(className, { [iconStyleName]: !!icon })}
    />
  );
};
StickerWidget.displayName = 'StickerWidget';
